class BlancCollectionsTwo extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.desktop = this.querySelector('[data-desktop-interface]');
    this.desktopCarousel = this.querySelector('[data-desktop-carousel]');
    this.desktopTitle = this.querySelector('[data-desktop-title]');
    this.desktopTitleCurrent = this.querySelector('[data-desktop-title-current]');
    this.desktopTitleIncoming = this.querySelector('[data-desktop-title-incoming]');
    this.desktopExplore = this.querySelector('[data-desktop-explore]');
    this.desktopCurrentCounter = this.querySelector('[data-desktop-current-counter]');
    this.desktopNextIndex = this.querySelector('[data-desktop-next-index-current]');
    this.desktopTagline = this.querySelector('[data-desktop-tagline]');
    this.desktopCurrentMeta = this.querySelector('[data-desktop-current-meta]');
    this.desktopSlides = [...this.querySelectorAll('[data-desktop-slide]')];
    this.desktopPreviousButtons = [...this.querySelectorAll('[data-desktop-previous]')];
    this.desktopNextButtons = [...this.querySelectorAll('[data-desktop-next]')];
    this.navItems = [...this.querySelectorAll('[data-desktop-nav]')];
    this.navIndicator = this.querySelector('[data-desktop-nav-indicator]');
    this.desktopNav = this.navIndicator?.parentElement;
    this.mobile = this.querySelector('[data-mobile-interface]');
    this.mobileLink = this.querySelector('[data-mobile-link]');
    this.mobileTitle = this.querySelector('[data-mobile-title]');
    this.mobileTitleCurrent = this.querySelector('[data-mobile-title-current]');
    this.mobileTitleIncoming = this.querySelector('[data-mobile-title-incoming]');
    this.mobileTitleVersion = 0;
    this.mobileTagline = this.querySelector('[data-mobile-tagline]');
    this.mobileImage = this.querySelector('[data-mobile-image]');
    this.mobileHeroStage = this.querySelector('[data-mobile-hero-stage]');
    this.mobileHero = this.querySelector('[data-mobile-hero]');
    this.mobilePreview = null;
    this.mobileAnimations = [];
    this.mobileMotionVersion = 0;
    this.mobileDragOffset = 0;
    this.mobileDragDirection = 0;
    this.mobilePreloads = new Map();
    this.mobileNearViewport = false;
    this.counter = this.querySelector('[data-counter-current]');
    this.counterName = this.querySelector('[data-counter-name]');
    this.progress = this.querySelector('[data-progress-thumb]');
    this.liveRegion = this.querySelector('[data-live-region]');
    this.mobilePreviousButtons = [...this.querySelectorAll('[data-mobile-previous]')];
    this.mobileNextButtons = [...this.querySelectorAll('[data-mobile-next]')];
    this.boundEvents = [];
    this.pendingRequest = null;
    this.isAnimating = false;
    this.desktopPointer = null;
    this.mobilePointer = null;
    this.desktopSuppressUntil = 0;
    this.mobileSuppressUntil = 0;
    this.titleVersion = 0;
    this.navFrame = null;
    this.metaAlignmentFrame = null;
    this.motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');

    try {
      this.data = JSON.parse(this.querySelector('[data-collection-data]')?.textContent || '[]');
    } catch (error) {
      this.data = [];
    }
    if (!this.data.length) return;

    this.activeIndex = this.normaliseIndex(Number(this.dataset.initialIndex) || 0);
    this.bindEvents();
    this.renderCurrentState({ immediate: true, announce: false });
    this.observeLayout();
    this.observeMobileArtwork();
    this.scheduleDesktopMetaAlignment();
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected) this.scheduleDesktopMetaAlignment();
      });
    }
  }

  disconnectedCallback() {
    this.boundEvents.forEach(({ element, type, handler, options }) => element.removeEventListener(type, handler, options));
    this.boundEvents = [];
    this.resizeObserver?.disconnect();
    this.mobileIntersectionObserver?.disconnect();
    this.resetMobileMotion();
    this.mobileTitleVersion++;
    this.mobilePreloads.clear();
    if (this.navFrame) cancelAnimationFrame(this.navFrame);
    if (this.metaAlignmentFrame) cancelAnimationFrame(this.metaAlignmentFrame);
    this.desktopCarousel?.classList.remove('is-collection-priming', 'is-collection-crossfading', 'is-crossfade-revealed');
    this.desktopCarousel?.querySelector('.is-crossfade-ghost')?.remove();
    this.desktopSlides?.forEach((slide) => slide.classList.remove('is-crossfade-incoming', 'is-crossfade-neighbor', 'is-crossfade-outgoing-side'));
    this.isAnimating = false;
    if (this.legacyMotionHandler) this.motionQuery?.removeListener?.(this.legacyMotionHandler);
    this.initialised = false;
  }

  listen(element, type, handler, options) {
    if (!element) return;
    element.addEventListener(type, handler, options);
    this.boundEvents.push({ element, type, handler, options });
  }

  get reducedMotion() {
    return Boolean(this.motionQuery?.matches);
  }

  get desktopViewport() {
    return window.matchMedia?.('(min-width: 820px)').matches;
  }

  normaliseIndex(index) {
    const count = this.data.length;
    return ((index % count) + count) % count;
  }

  directionFor(targetIndex) {
    const target = this.normaliseIndex(targetIndex);
    const forwardDistance = (target - this.activeIndex + this.data.length) % this.data.length;
    const backwardDistance = (this.activeIndex - target + this.data.length) % this.data.length;
    return forwardDistance <= backwardDistance ? 1 : -1;
  }

  bindEvents() {
    this.desktopPreviousButtons.forEach((button) => this.listen(button, 'click', () => this.requestIndex(this.activeIndex - 1, -1)));
    this.desktopNextButtons.forEach((button) => this.listen(button, 'click', () => this.requestIndex(this.activeIndex + 1, 1)));
    this.mobilePreviousButtons.forEach((button) => this.listen(button, 'click', () => this.requestIndex(this.activeIndex - 1, -1)));
    this.mobileNextButtons.forEach((button) => this.listen(button, 'click', () => this.requestIndex(this.activeIndex + 1, 1)));
    // Pointer activation must not leave a circular focus ring; keyboard focus stays visible.
    [...this.mobilePreviousButtons, ...this.mobileNextButtons].forEach((button) => {
      this.listen(button, 'pointerup', () => button.blur());
      this.listen(button, 'click', (event) => {
        if (event.detail > 0) button.blur();
      });
    });

    this.listen(document, 'keydown', (event) => this.onDocumentKeydown(event));

    this.listen(this.desktop, 'pointerup', () => {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement)) return;
      const shouldReleasePointerFocus = active === this.desktop
        || active.matches('[data-desktop-previous]')
        || active.matches('[data-desktop-next]')
        || active.matches('[data-desktop-nav]');
      if (shouldReleasePointerFocus && this.desktop.contains(active)) active.blur();
    });

    this.navItems.forEach((item) => this.listen(item, 'click', (event) => {
      const index = Number(item.dataset.index);
      if (!Number.isInteger(index) || index === this.activeIndex) return;
      event.preventDefault();
      this.requestIndex(index, index > this.activeIndex ? 1 : -1, 'indexed');
    }));

    this.listen(this.desktopCarousel, 'pointerdown', (event) => this.onDesktopPointerDown(event));
    this.listen(this.desktopCarousel, 'pointermove', (event) => this.onDesktopPointerMove(event));
    this.listen(this.desktopCarousel, 'pointerup', (event) => this.onDesktopPointerUp(event));
    this.listen(this.desktopCarousel, 'pointercancel', () => this.resetDesktopDrag());
    this.listen(this.desktopCarousel, 'click', (event) => {
      if (Date.now() < this.desktopSuppressUntil) {
        event.preventDefault();
        event.stopPropagation();
        this.desktopSuppressUntil = 0;
      }
    }, true);

    this.listen(this.mobile, 'pointerdown', (event) => this.onMobilePointerDown(event));
    this.listen(this.mobile, 'pointermove', (event) => this.onMobilePointerMove(event));
    this.listen(this.mobile, 'pointerup', (event) => this.onMobilePointerUp(event));
    this.listen(this.mobile, 'pointercancel', () => this.onMobilePointerCancel());
    this.listen(this.mobileLink, 'click', (event) => {
      if (Date.now() < this.mobileSuppressUntil) {
        event.preventDefault();
        this.mobileSuppressUntil = 0;
      }
    });

    this.listen(document, 'shopify:block:select', (event) => {
      const block = event.target instanceof Element ? event.target.closest('[data-shopify-editor-block]') : null;
      if (!block || !this.contains(block)) return;
      const index = Number(block.dataset.index ?? block.closest('[data-slide-index]')?.dataset.slideIndex);
      if (Number.isInteger(index)) this.requestIndex(index, index > this.activeIndex ? 1 : -1, 'indexed');
    });

    const motionHandler = () => {
      if (!this.desktopViewport) {
        this.resetMobileMotion();
        this.mobilePointer = null;
        this.isAnimating = false;
        this.pendingRequest = null;
      }
      this.renderCurrentState({ immediate: true, announce: false });
    };
    if (this.motionQuery?.addEventListener) this.listen(this.motionQuery, 'change', motionHandler);
    else if (this.motionQuery?.addListener) {
      this.legacyMotionHandler = motionHandler;
      this.motionQuery.addListener(motionHandler);
    }
  }

  isKeyboardContext(target) {
    const instances = [...document.querySelectorAll('blanc-collections-2')].filter((instance) => instance.data?.length);
    const focusedElement = target instanceof Element ? target : document.activeElement;
    const focusedInstance = instances.find((instance) => instance.contains(focusedElement));
    if (focusedInstance) return focusedInstance === this;

    const getVisibility = (instance) => {
      const rect = instance.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
      const referenceHeight = Math.min(rect.height, viewportHeight);
      return referenceHeight > 0 ? visibleHeight / referenceHeight : 0;
    };

    const visibility = getVisibility(this);
    if (visibility < 0.45) return false;
    const strongestInstance = instances.reduce((strongest, instance) => {
      if (!strongest || getVisibility(instance) > getVisibility(strongest)) return instance;
      return strongest;
    }, null);
    return strongestInstance === this;
  }

  onDocumentKeydown(event) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    const target = event.target instanceof Element ? event.target : document.activeElement;
    const editingTarget = target?.closest('input, textarea, select, [contenteditable], [role="textbox"]');
    if (editingTarget) return;
    const button = target?.closest('button');
    if (button && !button.matches('[data-desktop-previous], [data-desktop-next], [data-mobile-previous], [data-mobile-next]')) return;
    if (button && !this.contains(button)) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    if (!this.isKeyboardContext(target)) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'ArrowLeft') {
      this.requestIndex(this.activeIndex - 1, -1);
    } else if (event.key === 'ArrowRight') {
      this.requestIndex(this.activeIndex + 1, 1);
    } else if (event.key === 'Home') {
      this.requestIndex(0, -1, 'indexed');
    } else if (event.key === 'End') {
      this.requestIndex(this.data.length - 1, 1, 'indexed');
    }
  }

  observeLayout() {
    this.scheduleNavIndicator();
    const update = () => {
      this.scheduleNavIndicator();
      this.scheduleDesktopMetaAlignment();
    };
    this.listen(window, 'resize', update, { passive: true });
    if (window.ResizeObserver && this.desktopNav) {
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(this.desktopNav);
      if (this.desktopCarousel) this.resizeObserver.observe(this.desktopCarousel);
    }
  }

  scheduleDesktopMetaAlignment() {
    if (this.metaAlignmentFrame) cancelAnimationFrame(this.metaAlignmentFrame);
    this.metaAlignmentFrame = requestAnimationFrame(() => {
      this.metaAlignmentFrame = null;
      this.alignDesktopMetadata();
    });
  }

  alignDesktopMetadata() {
    if (!this.desktopViewport) return;
    const meta = this.desktopCurrentMeta;
    const tagline = this.desktopTagline;
    const arrow = this.desktopPreviousButtons[0];
    const lane = arrow?.closest('.blanc-collections-2__desktop-side-lane--left');
    if (!meta || !tagline || !arrow || !lane) return;

    const computed = getComputedStyle(tagline);
    const lineHeight = parseFloat(computed.lineHeight);
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;

    const metaRect = meta.getBoundingClientRect();
    const taglineRect = tagline.getBoundingClientRect();
    const arrowRect = arrow.getBoundingClientRect();
    const laneRect = lane.getBoundingClientRect();
    const taglineTopInsideMeta = taglineRect.top - metaRect.top;
    const arrowCentreInsideLane = (arrowRect.top + (arrowRect.height / 2)) - laneRect.top;
    const desiredMetaTop = arrowCentreInsideLane - taglineTopInsideMeta - (lineHeight * 1.5);
    meta.style.setProperty('--blanc-collections-2-meta-top', `${desiredMetaTop}px`);
  }

  onDesktopPointerDown(event) {
    if (this.isAnimating || event.button !== 0 || event.target.closest('button, .blanc-collections-2__desktop-explore')) return;
    this.desktopPointer = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), dragging: false, cancelled: false };
    this.desktopCarousel?.setPointerCapture?.(event.pointerId);
  }

  onDesktopPointerMove(event) {
    const pointer = this.desktopPointer;
    if (!pointer || pointer.id !== event.pointerId || pointer.cancelled) return;
    const deltaX = event.clientX - pointer.x;
    const deltaY = event.clientY - pointer.y;
    if (!pointer.dragging) {
      if (Math.abs(deltaX) < 8) return;
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        pointer.cancelled = true;
        return;
      }
      pointer.dragging = true;
      this.desktopCarousel.classList.add('is-dragging');
    }
    event.preventDefault();
    const resisted = Math.max(-116, Math.min(116, deltaX * .58));
    this.desktopCarousel.style.setProperty('--blanc-collections-2-desktop-drag-offset', `${resisted}px`);
    this.desktopCarousel.style.setProperty('--blanc-collections-2-desktop-adjacent-drag-offset', `${resisted}px`);
  }

  onDesktopPointerUp(event) {
    const pointer = this.desktopPointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    const deltaX = event.clientX - pointer.x;
    const elapsed = Math.max(1, performance.now() - pointer.time);
    const velocity = Math.abs(deltaX / elapsed);
    const shouldNavigate = pointer.dragging && (Math.abs(deltaX) >= 64 || velocity >= .45);
    const direction = deltaX < 0 ? 1 : -1;
    const didDrag = pointer.dragging;
    this.resetDesktopDrag();
    if (didDrag) this.desktopSuppressUntil = Date.now() + 450;
    if (shouldNavigate) this.requestIndex(this.activeIndex + direction, direction);
  }

  resetDesktopDrag() {
    if (!this.desktopPointer) return;
    this.desktopPointer = null;
    this.desktopCarousel?.classList.remove('is-dragging');
    this.desktopCarousel?.style.removeProperty('--blanc-collections-2-desktop-drag-offset');
    this.desktopCarousel?.style.removeProperty('--blanc-collections-2-desktop-adjacent-drag-offset');
  }

  observeMobileArtwork() {
    if (!this.mobile || !('IntersectionObserver' in window)) return;
    this.mobileIntersectionObserver = new IntersectionObserver((entries) => {
      this.mobileNearViewport = entries.some((entry) => entry.isIntersecting);
      if (this.mobileNearViewport && !this.desktopViewport) this.preloadMobileNeighbors();
    }, { rootMargin: '250px 0px' });
    this.mobileIntersectionObserver.observe(this.mobile);
  }

  preloadMobileNeighbors() {
    if (!this.mobileNearViewport || this.data.length < 2) return;
    const next = new Map();
    for (const step of [-1, 1]) {
      const index = this.normaliseIndex(this.activeIndex + step);
      const active = this.data[index];
      if (next.has(index) || !active?.image) continue;
      const image = this.mobilePreloads.get(index) || new Image();
      if (!this.mobilePreloads.has(index)) {
        image.decoding = 'async';
        image.sizes = '(max-width: 599px) min(71vw, 330px), min(52vw, 420px)';
        image.srcset = active.srcset || '';
        image.src = active.image;
      }
      next.set(index, image);
    }
    this.mobilePreloads = next;
  }

  onMobilePointerDown(event) {
    if (this.desktopViewport || this.isAnimating || this.data.length < 2 || (event.button !== undefined && event.button !== 0)) return;
    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest('[data-mobile-previous], [data-mobile-next]');
    if (control) {
      this.mobilePointer = null;
      return;
    }
    const interactive = target?.closest('button, a, input, textarea, select, [contenteditable], [role="button"], [role="link"], [role="textbox"]');
    if (interactive && interactive !== this.mobileLink) return;
    this.mobilePointer = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), dragging: false, cancelled: false };
    this.mobile?.setPointerCapture?.(event.pointerId);
  }

  onMobilePointerMove(event) {
    const pointer = this.mobilePointer;
    if (!pointer || pointer.id !== event.pointerId || pointer.cancelled) return;
    const deltaX = event.clientX - pointer.x;
    const deltaY = event.clientY - pointer.y;
    if (!pointer.dragging) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        pointer.cancelled = true;
        return;
      }
      if (Math.abs(deltaX) < 8) return;
      pointer.dragging = true;
    }
    const direction = deltaX < 0 ? 1 : -1;
    if (!deltaX) return;
    this.prepareMobilePreview(this.normaliseIndex(this.activeIndex + direction));
    this.setMobileDragOffset(deltaX, direction);
  }

  onMobilePointerUp(event) {
    const pointer = this.mobilePointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    this.mobilePointer = null;
    const deltaX = event.clientX - pointer.x;
    const deltaY = event.clientY - pointer.y;
    const swipeWithoutMove = Math.abs(deltaX) >= 44 && Math.abs(deltaX) > Math.abs(deltaY);
    if (pointer.cancelled || (!pointer.dragging && !swipeWithoutMove)) return;
    this.mobileSuppressUntil = Date.now() + 500;
    const direction = deltaX < 0 ? 1 : -1;
    const width = this.mobileHeroStage?.getBoundingClientRect().width || 375;
    const speed = Math.abs(deltaX) / Math.max(1, performance.now() - pointer.time);
    const shouldNavigate = Math.abs(deltaX) >= Math.min(70, width * .18) || (Math.abs(deltaX) >= 18 && speed >= .55);
    if (!deltaX) {
      this.animateMobileSnapBack();
      return;
    }
    this.prepareMobilePreview(this.normaliseIndex(this.activeIndex + direction));
    this.setMobileDragOffset(deltaX, direction);
    if (shouldNavigate) this.requestIndex(this.activeIndex + direction, direction);
    else this.animateMobileSnapBack();
  }

  onMobilePointerCancel() {
    if (this.mobilePointer?.dragging) this.animateMobileSnapBack();
    this.mobilePointer = null;
  }

  prepareMobilePreview(index) {
    if (!this.mobileHeroStage || !this.mobileHero) return;
    if (this.mobilePreview?.dataset.collectionIndex === String(index)) return;
    this.mobilePreview?.remove();
    const preview = this.mobileHero.cloneNode(true);
    preview.removeAttribute('data-mobile-hero');
    preview.classList.add('blanc-collections-2__mobile-hero-preview');
    preview.setAttribute('aria-hidden', 'true');
    preview.dataset.collectionIndex = String(index);
    preview.querySelector('[data-mobile-image]')?.removeAttribute('data-mobile-image');
    const active = this.data[index];
    this.setImage(preview.querySelector('.blanc-collections-2__mobile-image'), active, '(max-width: 599px) min(71vw, 330px), min(52vw, 420px)');
    this.mobilePreview = preview;
    this.mobileHeroStage.appendChild(preview);
  }

  setMobileDragOffset(offset, direction) {
    const width = this.mobileHeroStage?.getBoundingClientRect().width || 375;
    const bounded = Math.max(-width, Math.min(width, offset));
    this.mobileDragOffset = bounded;
    this.mobileDragDirection = direction;
    if (this.mobileHero) this.mobileHero.style.transform = `translate3d(${bounded}px, 0, 0)`;
    if (this.mobilePreview) this.mobilePreview.style.transform = `translate3d(${direction * width + bounded}px, 0, 0)`;
  }

  animateMobileElements(destination, duration) {
    const width = this.mobileHeroStage?.getBoundingClientRect().width || 375;
    const direction = this.mobileDragDirection;
    const start = this.mobileDragOffset;
    const options = { duration, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' };
    const animate = (element, from, to) => element.animate([
      { transform: `translate3d(${from}px, 0, 0)` },
      { transform: `translate3d(${to}px, 0, 0)` }
    ], options);
    this.mobileAnimations = [
      animate(this.mobileHero, start, destination),
      animate(this.mobilePreview, direction * width + start, direction * width + destination)
    ];
    return Promise.all(this.mobileAnimations.map((animation) => animation.finished.catch(() => {})));
  }

  animateMobileSnapBack() {
    if (!this.mobilePreview || !this.mobileHero?.animate) {
      this.resetMobileMotion();
      return;
    }
    this.isAnimating = true;
    const version = ++this.mobileMotionVersion;
    this.animateMobileElements(0, 280).then(() => {
      if (!this.isConnected || version !== this.mobileMotionVersion) return;
      this.finishMobileNavigation();
    });
  }

  beginMobileTransition(target, direction) {
    if (!this.mobileHeroStage || !this.mobileHero || !this.mobileHero.animate) {
      this.resetMobileMotion();
      this.activeIndex = target;
      this.renderCurrentState({ immediate: true, announce: true });
      return;
    }
    this.prepareMobilePreview(target);
    if (!this.mobilePreview) {
      this.resetMobileMotion();
      this.activeIndex = target;
      this.renderCurrentState({ immediate: true, announce: true });
      return;
    }
    this.setMobileTitle(this.data[target].name, true);
    const start = this.mobileDragDirection === direction ? this.mobileDragOffset : 0;
    this.setMobileDragOffset(start, direction);
    this.isAnimating = true;
    const version = ++this.mobileMotionVersion;
    const width = this.mobileHeroStage.getBoundingClientRect().width || 375;
    const remaining = Math.max(0, 1 - Math.abs(start) / width);
    const duration = Math.max(220, Math.round(420 * remaining));
    this.animateMobileElements(-direction * width, duration).then(() => {
      if (!this.isConnected || version !== this.mobileMotionVersion) return;
      this.activeIndex = target;
      this.renderCurrentState({ immediate: false, announce: true });
      this.preloadMobileNeighbors();
      this.finishMobileNavigation();
    });
  }

  resetMobileMotion() {
    this.mobileMotionVersion++;
    this.mobileAnimations.forEach((animation) => animation.cancel());
    this.mobileAnimations = [];
    this.mobilePreview?.remove();
    this.mobilePreview = null;
    this.mobileHero?.style.removeProperty('transform');
    this.mobileDragOffset = 0;
    this.mobileDragDirection = 0;
  }

  finishMobileNavigation() {
    this.resetMobileMotion();
    this.isAnimating = false;
    const pending = this.pendingRequest;
    this.pendingRequest = null;
    if (pending && pending.index !== this.activeIndex) {
      this.requestIndex(pending.index, pending.direction, pending.navigation);
    }
  }

  requestIndex(index, requestedDirection, navigation = 'sequential') {
    if (!this.data.length) return;
    const target = this.normaliseIndex(index);
    if (target === this.activeIndex && !this.isAnimating) return;
    if (this.isAnimating) {
      // Keep the latest requested selection and its navigation mode.
      this.pendingRequest = { index: target, direction: requestedDirection, navigation };
      return;
    }
    // Numbered tabs follow their visual order; arrows and swipes wrap.
    const direction = navigation === 'indexed'
      ? (target > this.activeIndex ? 1 : -1)
      : requestedDirection || this.directionFor(target);
    if (this.reducedMotion) {
      this.resetMobileMotion();
      this.activeIndex = target;
      this.renderCurrentState({ immediate: true, announce: true });
      return;
    }
    if (!this.desktopViewport) {
      this.beginMobileTransition(target, direction);
      return;
    }
    if (navigation === 'indexed' && Math.abs(target - this.activeIndex) > 1) {
      this.beginDesktopCrossfade(target, direction);
    } else {
      this.beginDesktopTransition(target, direction);
    }
  }

  finishDesktopNavigation() {
    this.desktop?.classList.remove('is-collection-transitioning');
    this.isAnimating = false;
    const pending = this.pendingRequest;
    this.pendingRequest = null;
    if (pending && pending.index !== this.activeIndex) {
      this.requestIndex(pending.index, pending.direction, pending.navigation);
    }
  }

  async prepareDesktopJumpImages(indices) {
    const images = new Set(indices.flatMap((index) =>
      [...(this.getSlide(index)?.querySelectorAll('img') || [])]
    ));
    if (!images.size) return;

    const loading = Promise.all([...images].map((image) => {
      image.loading = 'eager';
      return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
    }));
    // Never hold navigation indefinitely for a missing or slow image.
    let timeout;
    await Promise.race([
      loading,
      new Promise((resolve) => { timeout = window.setTimeout(resolve, 1600); }),
    ]);
    window.clearTimeout(timeout);
  }

  async beginDesktopCrossfade(target, direction) {
    const previousActive = this.activeIndex;
    const outgoing = this.getSlide(previousActive);
    const track = this.desktopCarousel?.querySelector('.blanc-collections-2__desktop-media-track');
    if (!outgoing || !track) return;
    const newPrevious = this.normaliseIndex(target - 1);
    const newNext = this.normaliseIndex(target + 1);
    const oldPrevious = this.normaliseIndex(previousActive - 1);
    const oldNext = this.normaliseIndex(previousActive + 1);
    this.isAnimating = true;
    this.pendingRequest = null;
    let ghost;

    try {
      // Hidden/lazy images must be requested before their fade begins.
      await this.prepareDesktopJumpImages([target, newPrevious, newNext]);
      if (!this.isConnected) return;
      // If the user selected another number while loading, honour the
      // latest choice instead of playing an obsolete intermediate jump.
      if (this.pendingRequest && this.pendingRequest.index !== target) return;

      ghost = outgoing.cloneNode(true);
      ghost.removeAttribute('data-desktop-slide');
      ghost.removeAttribute('data-slide-index');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.setAttribute('inert', '');
      ghost.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      ghost.classList.add('is-crossfade-ghost');

      this.desktop?.classList.add('is-collection-transitioning');
      this.desktopCarousel.classList.add('is-collection-priming', 'is-collection-crossfading');
      this.desktopCarousel.style.setProperty('--blanc-collections-2-crossfade-offset', `${direction * 24}px`);
      track.appendChild(ghost);

      const resting = new Map([
        [target, 'is-active'],
        [newPrevious, 'is-previous'],
        [newNext, 'is-next'],
      ]);
      // Only old side artworks that are not part of the destination
      // scene should fade away; do not replace the visible new ones.
      for (const [index, state] of [[oldPrevious, 'is-previous'], [oldNext, 'is-next']]) {
        if (!resting.has(index) && index !== previousActive) resting.set(index, state);
      }
      this.applySlideStates(resting);
      this.getSlide(target)?.classList.add('is-crossfade-incoming');
      this.getSlide(newPrevious)?.classList.add('is-crossfade-neighbor');
      this.getSlide(newNext)?.classList.add('is-crossfade-neighbor');
      for (const index of [oldPrevious, oldNext]) {
        if (!new Set([target, newPrevious, newNext, previousActive]).has(index)) {
          this.getSlide(index)?.classList.add('is-crossfade-outgoing-side');
        }
      }
      this.desktopCarousel.getBoundingClientRect();

      await new Promise((resolve) => requestAnimationFrame(() => {
        if (!this.isConnected) { resolve(); return; }
        this.desktopCarousel.classList.remove('is-collection-priming');
        this.desktopCarousel.getBoundingClientRect();
        this.desktopCarousel.classList.add('is-crossfade-revealed');
        this.activeIndex = target;
        this.updateContent({ animate: true, announce: true, direction });
        this.waitForTransition(ghost, { property: 'opacity', fallback: 760 }).then(resolve);
      }));
    } catch (error) {
      console.error('BLANC collection crossfade failed:', error);
      if (this.isConnected) {
        this.activeIndex = target;
        this.renderCurrentState({ immediate: true, announce: true });
      }
    } finally {
      if (this.isConnected && this.desktopCarousel) {
        // Snap only invisible cleanup changes; never slide an old frame
        // into a newly selected neighbour position after the reveal.
        this.desktopCarousel.classList.add('is-collection-priming');
        this.applyDesktopSlideStates();
        this.desktopCarousel.getBoundingClientRect();
      }
      ghost?.remove();
      this.desktopSlides.forEach((slide) =>
        slide.classList.remove('is-crossfade-incoming', 'is-crossfade-neighbor', 'is-crossfade-outgoing-side')
      );
      this.desktopCarousel?.classList.remove('is-crossfade-revealed', 'is-collection-crossfading');
      this.desktopCarousel?.style.removeProperty('--blanc-collections-2-crossfade-offset');
      this.desktopCarousel?.getBoundingClientRect();
      this.desktopCarousel?.classList.remove('is-collection-priming');
      this.finishDesktopNavigation();
    }
  }

  beginDesktopTransition(target, direction) {
    const previousActive = this.activeIndex;
    const targetSlide = this.getSlide(target);
    if (!targetSlide) return;
    this.isAnimating = true;
    this.pendingRequest = null;
    this.desktop?.classList.add('is-collection-transitioning');
    const oldPrevious = this.normaliseIndex(previousActive - 1);
    const oldNext = this.normaliseIndex(previousActive + 1);
    const newPrevious = this.normaliseIndex(target - 1);
    const newNext = this.normaliseIndex(target + 1);
    const primeStates = new Map();
    if (this.data.length === 2) {
      primeStates.set(previousActive, 'is-active');
      primeStates.set(target, direction === 1 ? 'is-next' : 'is-previous');
    } else if (direction === 1) {
      primeStates.set(oldPrevious, 'is-previous');
      primeStates.set(previousActive, 'is-active');
      primeStates.set(target, 'is-next');
      primeStates.set(newNext, 'is-far-next');
    } else {
      primeStates.set(newPrevious, 'is-far-previous');
      primeStates.set(target, 'is-previous');
      primeStates.set(previousActive, 'is-active');
      primeStates.set(oldNext, 'is-next');
    }
    this.desktopCarousel?.classList.add('is-collection-priming');
    this.applySlideStates(primeStates);
    this.desktopCarousel?.getBoundingClientRect();

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.desktopCarousel?.classList.remove('is-collection-priming');
      this.desktopCarousel?.getBoundingClientRect();

      const transitionalStates = new Map();
      if (this.data.length === 2) {
        transitionalStates.set(target, 'is-active');
        transitionalStates.set(previousActive, direction === 1 ? 'is-previous' : 'is-next');
      } else if (direction === 1) {
        transitionalStates.set(oldPrevious, 'is-far-previous');
        transitionalStates.set(previousActive, 'is-previous');
        transitionalStates.set(target, 'is-active');
        transitionalStates.set(newNext, 'is-next');
      } else {
        transitionalStates.set(newPrevious, 'is-previous');
        transitionalStates.set(target, 'is-active');
        transitionalStates.set(previousActive, 'is-next');
        transitionalStates.set(oldNext, 'is-far-next');
      }
      this.applySlideStates(transitionalStates);
      this.activeIndex = target;
      this.updateContent({ animate: true, announce: true, direction });

      this.waitForTransition(targetSlide).then(() => {
        if (!this.isConnected) return;
        // On non-adjacent jumps a newly revealed neighbour may still be
        // hidden at the default centre position. Reset the resting slide
        // positions without transitions, then restore animation for the
        // next interaction so no frame flicks out from the centre.
        this.desktopCarousel?.classList.add('is-collection-priming');
        this.applyDesktopSlideStates();
        this.desktopCarousel?.getBoundingClientRect();
        this.desktopCarousel?.classList.remove('is-collection-priming');
        this.finishDesktopNavigation();
      });
    });
  }

  waitForTransition(element, { property = 'transform', fallback: fallbackDuration = 980 } = {}) {
    return new Promise((resolve) => {
      let complete = false;
      const finish = () => {
        if (complete) return;
        complete = true;
        element.removeEventListener('transitionend', onEnd);
        window.clearTimeout(fallback);
        resolve();
      };
      const onEnd = (event) => {
        if (event.target === element && event.propertyName === property) finish();
      };
      const fallback = window.setTimeout(finish, fallbackDuration);
      element.addEventListener('transitionend', onEnd);
    });
  }

  getSlide(index) {
    return this.desktopSlides.find((slide) => Number(slide.dataset.slideIndex) === index);
  }

  applySlideStates(states) {
    this.desktopSlides.forEach((slide) => {
      const index = Number(slide.dataset.slideIndex);
      this.applySlideState(slide, states.get(index) || 'is-hidden');
    });
  }

  applySlideState(slide, state) {
    slide.classList.remove('is-far-previous', 'is-previous', 'is-active', 'is-next', 'is-far-next', 'is-hidden');
    slide.classList.add(state);
    const active = state === 'is-active';
    const link = slide.querySelector('[data-desktop-slide-link]');
    if (active) {
      slide.removeAttribute('aria-hidden');
      link?.removeAttribute('tabindex');
    } else {
      slide.setAttribute('aria-hidden', 'true');
      link?.setAttribute('tabindex', '-1');
    }
  }

  applyDesktopSlideStates() {
    const states = new Map();
    states.set(this.activeIndex, 'is-active');
    if (this.data.length > 1) states.set(this.normaliseIndex(this.activeIndex + 1), 'is-next');
    if (this.data.length > 2) states.set(this.normaliseIndex(this.activeIndex - 1), 'is-previous');
    this.applySlideStates(states);
  }

  renderCurrentState({ immediate = false, announce = true } = {}) {
    this.applyDesktopSlideStates();
    this.updateContent({ animate: !immediate, announce });
  }

  updateContent({ animate, announce, direction = 1 }) {
    const active = this.data[this.activeIndex];
    if (!active) return;
    const number = String(this.activeIndex + 1).padStart(2, '0');
    const nextIndex = this.normaliseIndex(this.activeIndex + 1);
    this.setDesktopTitle(active.editorialTitle || active.name, animate);
    if (this.desktopExplore) {
      this.desktopExplore.href = active.url;
    }
    this.swapMechanicalCounter(this.desktopCurrentCounter, number, { animate, direction, duration: 210, delay: 100 });
    if (this.desktopNextIndex) this.desktopNextIndex.textContent = String(nextIndex + 1).padStart(2, '0');
    this.swapText(this.desktopTagline, active.tagline || '', { animate, distance: 0, duration: 210, delay: 80, stableLayer: true, animateHeight: true });

    this.setMobileTitle(active.name, animate);
    this.swapText(this.mobileTagline, active.tagline || '', { animate, distance: 0, duration: 190, delay: 0, stableLayer: true, animateHeight: true });
    if (this.mobileLink) {
      this.mobileLink.href = active.url;
      this.mobileLink.setAttribute('aria-label', `View ${active.name} collection`);
    }
    this.setImage(this.mobileImage, active, '(max-width: 599px) min(71vw, 330px), min(52vw, 420px)');
    this.swapText(this.counter, number, { animate, distance: 125, duration: 340, stableLayer: true });
    if (this.counterName) this.counterName.textContent = active.name.toUpperCase();
    if (this.progress) this.progress.style.transform = `translateX(${this.activeIndex * 100}%)`;

    this.navItems.forEach((item) => {
      const activeItem = Number(item.dataset.index) === this.activeIndex;
      item.classList.toggle('is-active', activeItem);
      if (activeItem) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });
    this.scheduleNavIndicator();
    if (announce && this.liveRegion) this.liveRegion.textContent = `${number} of ${String(this.data.length).padStart(2, '0')}, ${active.name}`;
  }

  createTitleWord(value) {
    const word = document.createElement('span');
    word.className = 'blanc-collections-2__desktop-title-word';
    word.textContent = String(value).toUpperCase();
    return word;
  }

  setDesktopTitle(value, animate) {
    if (!this.desktopTitle || !this.desktopTitleCurrent || !this.desktopTitleIncoming) return;
    const title = String(value || '').toUpperCase();
    if (this.desktopTitle.dataset.title === title) return;
    this.desktopTitle.dataset.title = title;
    this.desktopTitle.setAttribute('aria-label', this.data[this.activeIndex]?.name || title);
    const version = ++this.titleVersion;
    if (!animate || this.reducedMotion || !this.desktopTitleCurrent.childElementCount) {
      this.desktopTitle.classList.remove('is-transitioning', 'is-title-committing');
      this.desktopTitleCurrent.replaceChildren(this.createTitleWord(title));
      this.desktopTitleIncoming.replaceChildren();
      return;
    }
    this.desktopTitleIncoming.replaceChildren(this.createTitleWord(title));
    this.desktopTitle.classList.remove('is-transitioning', 'is-title-committing');
    this.desktopTitle.getBoundingClientRect();
    requestAnimationFrame(() => {
      if (!this.isConnected || version !== this.titleVersion) return;
      this.desktopTitle.classList.add('is-transitioning');
      this.waitForTransition(this.desktopTitleIncoming, { property: 'opacity', fallback: 420 }).then(() => {
        if (version !== this.titleVersion || !this.isConnected) return;
        this.desktopTitle.classList.add('is-title-committing');
        this.desktopTitleCurrent.replaceChildren(...this.desktopTitleIncoming.childNodes);
        this.desktopTitle.classList.remove('is-transitioning');
        this.desktopTitleIncoming.replaceChildren();
        this.desktopTitle.getBoundingClientRect();
        this.desktopTitle.classList.remove('is-title-committing');
      });
    });
  }

  setMobileTitle(value, animate) {
    if (!this.mobileTitle || !this.mobileTitleCurrent || !this.mobileTitleIncoming) return;
    const title = String(value || '').toUpperCase();
    if (this.mobileTitle.dataset.title === title && (animate || !this.mobileTitle.classList.contains('is-transitioning'))) return;
    this.mobileTitle.dataset.title = title;
    this.mobileTitle.setAttribute('aria-label', String(value || ''));
    const version = ++this.mobileTitleVersion;
    if (!animate || this.reducedMotion || !this.mobileTitleCurrent.textContent.trim()) {
      this.mobileTitle.classList.remove('is-transitioning', 'is-title-committing');
      this.mobileTitleCurrent.textContent = title;
      this.mobileTitleIncoming.textContent = '';
      return;
    }
    this.mobileTitleIncoming.textContent = title;
    this.mobileTitle.classList.remove('is-transitioning', 'is-title-committing');
    this.mobileTitle.getBoundingClientRect();
    requestAnimationFrame(() => {
      if (!this.isConnected || version !== this.mobileTitleVersion) return;
      this.mobileTitle.classList.add('is-transitioning');
      this.waitForTransition(this.mobileTitleIncoming, { property: 'opacity', fallback: 420 }).then(() => {
        if (!this.isConnected || version !== this.mobileTitleVersion) return;
        this.mobileTitle.classList.add('is-title-committing');
        this.mobileTitleCurrent.textContent = title;
        this.mobileTitle.classList.remove('is-transitioning');
        this.mobileTitleIncoming.textContent = '';
        this.mobileTitle.getBoundingClientRect();
        this.mobileTitle.classList.remove('is-title-committing');
      });
    });
  }

  swapText(element, value, { animate, distance, duration, delay = 0, stableLayer = false, animateHeight = false }) {
    if (!element) return;
    const text = String(value);
    const previousText = element.dataset.value ?? element.textContent;
    if (previousText === text) return;
    const startHeight = animateHeight ? element.getBoundingClientRect().height : 0;
    if (animateHeight && element._blancHeightAnimation) {
      element._blancHeightAnimation.cancel();
      element._blancHeightAnimation = null;
      element.style.removeProperty('height');
    }
    element.dataset.value = text;
    const version = Number(element.dataset.copyVersion || 0) + 1;
    element.dataset.copyVersion = String(version);
    if (!animate || this.reducedMotion || !element.textContent.trim()) {
      if (stableLayer) this.setStableCopyLayer(element, text);
      else element.textContent = text;
      return;
    }
    const outgoing = document.createElement('span');
    const incoming = document.createElement('span');
    outgoing.className = 'blanc-collections-2__copy-layer';
    incoming.className = 'blanc-collections-2__copy-layer';
    outgoing.textContent = previousText;
    incoming.textContent = text;
    if (animateHeight) {
      element.style.height = `${startHeight}px`;
      outgoing.style.alignSelf = 'start';
      incoming.style.alignSelf = 'start';
    }
    element.replaceChildren(outgoing, incoming);
    const endHeight = animateHeight ? incoming.getBoundingClientRect().height : 0;
    const unit = distance >= 20 ? '%' : 'px';
    const animationOptions = { duration, delay, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' };
    const outgoingAnimation = outgoing.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: `translateY(-${distance}${unit})` }], animationOptions);
    const incomingAnimation = incoming.animate([{ opacity: 0, transform: `translateY(${distance}${unit})` }, { opacity: 1, transform: 'translateY(0)' }], animationOptions);
    let heightAnimation = null;
    if (animateHeight && Math.abs(startHeight - endHeight) > 0.5) {
      heightAnimation = element.animate([{ height: `${startHeight}px` }, { height: `${endHeight}px` }], animationOptions);
      element._blancHeightAnimation = heightAnimation;
    } else if (animateHeight) {
      element.style.removeProperty('height');
    }
    Promise.all([
      outgoingAnimation.finished.catch(() => {}),
      incomingAnimation.finished.catch(() => {}),
      heightAnimation?.finished.catch(() => {}) || Promise.resolve()
    ]).then(() => {
      if (version === Number(element.dataset.copyVersion) && this.isConnected) {
        if (stableLayer) this.setStableCopyLayer(element, text);
        else element.textContent = text;
        if (animateHeight) {
          element.style.removeProperty('height');
          element._blancHeightAnimation = null;
        }
      }
    });
  }

  swapMechanicalCounter(element, value, { animate, direction = 1, duration = 210, delay = 100 }) {
    if (!element) return;
    const text = String(value);
    const previousText = element.dataset.value ?? element.textContent;
    if (previousText === text) return;

    element.dataset.value = text;
    const version = Number(element.dataset.copyVersion || 0) + 1;
    element.dataset.copyVersion = String(version);
    if (!animate || this.reducedMotion || !element.textContent.trim()) {
      this.setStableCopyLayer(element, text);
      return;
    }

    const outgoing = document.createElement('span');
    const incoming = document.createElement('span');
    outgoing.className = 'blanc-collections-2__copy-layer';
    incoming.className = 'blanc-collections-2__copy-layer';
    outgoing.textContent = previousText;
    incoming.textContent = text;
    element.replaceChildren(outgoing, incoming);

    const travel = direction < 0 ? -8 : 8;
    const animationOptions = {
      duration,
      delay,
      easing: 'cubic-bezier(.25, .8, .25, 1)',
      fill: 'both'
    };
    const outgoingAnimation = outgoing.animate([
      { offset: 0, opacity: 1, transform: 'translateY(0)' },
      { offset: .42, opacity: 1, transform: `translateY(${-travel * .25}px)` },
      { offset: .72, opacity: 0, transform: `translateY(${-travel}px)` },
      { offset: 1, opacity: 0, transform: `translateY(${-travel}px)` }
    ], animationOptions);
    const incomingAnimation = incoming.animate([
      { offset: 0, opacity: 0, transform: `translateY(${travel}px)` },
      { offset: .34, opacity: 0, transform: `translateY(${travel}px)` },
      { offset: .66, opacity: 1, transform: `translateY(${travel * .2}px)` },
      { offset: 1, opacity: 1, transform: 'translateY(0)' }
    ], animationOptions);

    Promise.all([
      outgoingAnimation.finished.catch(() => {}),
      incomingAnimation.finished.catch(() => {})
    ]).then(() => {
      if (version === Number(element.dataset.copyVersion) && this.isConnected) {
        this.setStableCopyLayer(element, text);
      }
    });
  }

  setStableCopyLayer(element, text) {
    const layer = document.createElement('span');
    layer.className = 'blanc-collections-2__copy-layer';
    layer.textContent = text;
    element.replaceChildren(layer);
  }

  setImage(container, active, sizes) {
    if (!container) return;
    if (active.image) {
      let image = container.querySelector('img');
      if (!image) {
        image = document.createElement('img');
      }
      // Defer below-fold artwork until the carousel is near the viewport.
      image.loading = 'lazy';
      image.decoding = 'async';
      image.srcset = active.srcset || '';
      image.sizes = sizes;
      image.alt = active.alt;
      if (!image.parentElement) container.replaceChildren(image);
      image.src = active.image;
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'blanc-collections-2__mobile-placeholder';
      placeholder.textContent = 'Mobile artwork not selected';
      container.replaceChildren(placeholder);
    }
  }

  scheduleNavIndicator() {
    if (!this.navIndicator || !this.desktopNav) return;
    if (this.navFrame) cancelAnimationFrame(this.navFrame);
    this.navFrame = requestAnimationFrame(() => {
      const active = this.navItems.find((item) => Number(item.dataset.index) === this.activeIndex);
      if (!active) return;
      const navRect = this.desktopNav.getBoundingClientRect();
      const itemRect = active.getBoundingClientRect();
      // Keep the indicator centred beneath the selected tab, not across
      // its entire grid column (which can be much wider than the label).
      const indicatorWidth = Math.min(110, itemRect.width);
      this.navIndicator.style.width = `${indicatorWidth}px`;
      this.navIndicator.style.transform = `translateX(${itemRect.left - navRect.left + (itemRect.width - indicatorWidth) / 2}px)`;
    });
  }
}

if (!customElements.get('blanc-collections-2')) {
  customElements.define('blanc-collections-2', BlancCollectionsTwo);
}
